import { useMemo, useCallback } from "react";
import type { Field } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";

export const useTaskFields = (
  formData: Partial<TaskManagerDTO>,
  workflowFields: Field[],
  task?: TaskManagerDTO,
  setFormData?: React.Dispatch<React.SetStateAction<Partial<TaskManagerDTO>>>
) => {
  const isEditMode = !!task;

  const initializedTaskFields = useMemo(() => {
    if (workflowFields.length === 0) return formData.taskFields || [];

    if (isEditMode && task?.taskFields) {
      return workflowFields.map((field) => {
        const existingField = task.taskFields?.find((tf) => tf.fieldId === field.id);
        return {
          fieldId: field.id,
          value: existingField?.value || "",
          type: field.type,
        };
      });
    }

    return workflowFields.map((field) => ({
      fieldId: Number(field.id),
      value: "",
      type: field.type,
    }));
  }, [workflowFields, isEditMode, task?.taskFields]);

  useMemo(() => {
    if (
      initializedTaskFields.length > 0 &&
      JSON.stringify(formData.taskFields) !== JSON.stringify(initializedTaskFields) &&
      setFormData
    ) {
      setFormData((prev) => ({
        ...prev,
        taskFields: initializedTaskFields,
      }));
    }
  }, [initializedTaskFields, setFormData]);

  const updateTaskFieldValue = useCallback(
    (fieldId: number, value: string, type: string) => {
      if (!setFormData) return;

      setFormData((prev) => {
        const existingFields = prev.taskFields || [];
        const fieldIndex = existingFields.findIndex((f) => f.fieldId === fieldId);

        const updatedFields = [...existingFields];
        if (fieldIndex >= 0) {
          updatedFields[fieldIndex] = { fieldId, value, type };
        } else {
          updatedFields.push({ fieldId, value, type });
        }

        return { ...prev, taskFields: updatedFields };
      });
    },
    [setFormData]
  );

  const getTaskFieldValue = useCallback(
    (fieldId: number): string => {
      return formData.taskFields?.find((f) => f.fieldId === fieldId)?.value || "";
    },
    [formData.taskFields]
  );

  const getTaskFieldValueAsArray = useCallback(
    (fieldId: number): string[] => {
      const value = getTaskFieldValue(fieldId);
      return value
        ? value
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [];
    },
    [getTaskFieldValue]
  );

  return {
    updateTaskFieldValue,
    getTaskFieldValue,
    getTaskFieldValueAsArray,
  };
};
